import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

const defaultState = {
  open: false,
  title: '',
  message: '',
  confirmButtonText: 'Confirm',
  cancelButtonText: 'Cancel',
  confirmButtonColor: 'primary',
  cancelButtonColor: 'secondary',
  confirmButtonVariant: 'contained',
  cancelButtonVariant: 'outlined'
};

export function ConfirmProvider({ children }) {
  const [confirmState, setConfirmState] = useState(defaultState);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  /**
   * Promise-based confirm (drop-in replacement for window.confirm).
   * @returns {Promise<boolean>}
   */
  const confirm = useCallback((options = {}) => {
    const {
      title = 'Confirmation',
      message = '',
      confirmButtonText = 'Confirm',
      cancelButtonText = 'Cancel',
      confirmButtonColor = 'primary',
      cancelButtonColor = 'secondary',
      confirmButtonVariant = 'contained',
      cancelButtonVariant = 'outlined'
    } = typeof options === 'string' ? { message: options } : options;

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfirmState({
        open: true,
        title,
        message,
        confirmButtonText,
        cancelButtonText,
        confirmButtonColor,
        cancelButtonColor,
        confirmButtonVariant,
        cancelButtonVariant
      });
    });
  }, []);

  /** Callback-style API (kept for existing callers like LocalPlan) */
  const showConfirm = useCallback(
    ({
      title = 'Confirmation',
      message,
      onConfirm,
      onCancel = () => {},
      confirmButtonText = 'Confirm',
      cancelButtonText = 'Cancel',
      confirmButtonColor = 'primary',
      cancelButtonColor = 'secondary',
      confirmButtonVariant = 'contained',
      cancelButtonVariant = 'outlined'
    }) => {
      confirm({
        title,
        message,
        confirmButtonText,
        cancelButtonText,
        confirmButtonColor,
        cancelButtonColor,
        confirmButtonVariant,
        cancelButtonVariant
      }).then((ok) => {
        if (ok) onConfirm?.();
        else onCancel?.();
      });
    },
    [confirm]
  );

  return (
    <ConfirmContext.Provider value={{ confirm, showConfirm }}>
      {children}
      <Dialog
        open={confirmState.open}
        onClose={() => close(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">{confirmState.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">{confirmState.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => close(false)}
            color={confirmState.cancelButtonColor}
            variant={confirmState.cancelButtonVariant}
          >
            {confirmState.cancelButtonText}
          </Button>
          <Button
            onClick={() => close(true)}
            color={confirmState.confirmButtonColor}
            variant={confirmState.confirmButtonVariant}
            autoFocus
          >
            {confirmState.confirmButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
